import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../components/StyledTableComponents';
import { TRestaurantPaymentConfig } from '../../../PaymentProviders/schemas/payment-provider-schema';

type RestaurantConfigsListProps = {
  configs: TRestaurantPaymentConfig[];
  onEdit: (config: TRestaurantPaymentConfig) => void;
  onDelete: (config: TRestaurantPaymentConfig) => void;
  onSetDefault: (config: TRestaurantPaymentConfig) => void;
  settingDefaultId?: string | null;
};

const RestaurantConfigsList = ({
  configs,
  onEdit,
  onDelete,
  onSetDefault,
  settingDefaultId,
}: RestaurantConfigsListProps) => {
  if (!configs || configs.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography sx={styles.emptyText}>
          No payment provider configs yet. Add one to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Provider</StyledTableCell>
            <StyledTableCell>Provider ID</StyledTableCell>
            <StyledTableCell>Config Version</StyledTableCell>
            <StyledTableCell>Default</StyledTableCell>
            <StyledTableCell>Updated</StyledTableCell>
            <StyledTableCell align="right">Actions</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {configs.map((config) => (
            <StyledTableRow key={config.providerId}>
              <StyledTableCell sx={styles.providerCell}>
                {config.displayName || config.providerId}
              </StyledTableCell>
              <StyledTableCell sx={styles.monoCell}>{config.providerId}</StyledTableCell>
              <StyledTableCell>v{config.configVersion}</StyledTableCell>
              <StyledTableCell>
                {config.isActiveDefault ? (
                  <Chip
                    label="Default"
                    size="small"
                    icon={<StarIcon style={{ color: '#f9a825' }} />}
                    sx={{
                      backgroundColor: '#fff8e1',
                      color: '#8d6e00',
                      fontFamily: 'GilroySemiBold',
                      fontSize: '11px',
                    }}
                  />
                ) : (
                  <Tooltip title="Set as default">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onSetDefault(config)}
                        disabled={settingDefaultId === config.providerId}
                      >
                        <StarOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </StyledTableCell>
              <StyledTableCell sx={styles.dateCell}>
                {config.updatedAt
                  ? new Date(config.updatedAt).toLocaleString()
                  : '-'}
              </StyledTableCell>
              <StyledTableCell align="right">
                <Box sx={styles.actionBox}>
                  <Tooltip title="Edit client fields">
                    <IconButton size="small" onClick={() => onEdit(config)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete config">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(config)}
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RestaurantConfigsList;

const styles = {
  tableContainer: {
    boxShadow: 'none',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    borderRadius: '8px',
    height: '6rem',
  },
  emptyText: {
    color: '#999',
    fontSize: '14px',
    fontFamily: 'InterRegular',
  },
  providerCell: {
    fontFamily: 'GilroySemiBold',
  },
  monoCell: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: '#555',
  },
  dateCell: {
    whiteSpace: 'nowrap' as const,
    color: '#555',
    fontSize: 13,
  },
  actionBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '4px',
  },
};
